import { z } from "zod";
import { supportedProfileLocaleCodes } from "@/config/supported-locales";

const idSchema = z.string().regex(/^[0-9a-f]{32}$/u);
const utf8ByteLength = (value: string): number => new TextEncoder().encode(value).byteLength;
const withinUtf8Bytes =
  (maximum: number) =>
  (value: string): boolean =>
    utf8ByteLength(value) <= maximum;
export const clientRoleSchema = z.enum([
  "CUSTOMER",
  "CLEANER",
  "ORG_ADMIN",
  "ORG_WORKER",
  "LINEN_PROVIDER",
]);

const legalContentUrlSchema = z
  .string()
  .url()
  .refine((value) => {
    const url = new URL(value);
    return url.protocol === "https:" && url.username === "" && url.password === "";
  }, "Use an HTTPS URL without embedded credentials");

const isoDateTimeSchema = z.string().datetime({ offset: true });
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/u);
export const supportedProfileLocaleSchema = z.enum(supportedProfileLocaleCodes);

export const serviceCategorySchema = z.strictObject({
  id: idSchema,
  code: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  displayOrder: z.number().int().nonnegative(),
});

export const catalogServiceSchema = z.strictObject({
  id: idSchema,
  code: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  pricingUnit: z.enum(["JOB", "HOUR", "ROOM", "BEDROOM", "BATHROOM", "SQFT", "ITEM", "POUND"]),
  displayOrder: z.number().int().nonnegative(),
  category: z.strictObject({
    id: idSchema,
    code: z.string().min(1),
  }),
});

export const linenItemTypeSchema = z.strictObject({
  id: idSchema,
  code: z.string().min(1),
  name: z.string().min(1),
  group: z.enum(["SHEET", "PILLOWCASE", "DUVET", "TOWEL", "MAT", "OTHER"]),
  sizeCode: z.string().nullable(),
  widthCm: z.number().positive().nullable(),
  lengthCm: z.number().positive().nullable(),
});

export const currentUserSchema = z.strictObject({
  id: idSchema,
  partyId: idSchema,
  email: z.string().email(),
  emailVerified: z.boolean(),
  userStatus: z.enum(["INVITED", "ACTIVE", "LOCKED", "CLOSED"]),
  partyStatus: z.enum(["ACTIVE", "SUSPENDED", "CLOSED"]),
  displayName: z.string().min(1),
  profile: z.strictObject({
    givenName: z.string().min(1),
    familyName: z.string().min(1),
    phoneE164: z.string().nullable(),
    localeCode: supportedProfileLocaleSchema,
    timezoneRegion: z.string().min(1),
  }),
  roles: z.array(clientRoleSchema),
});

const validTimezone = (value: string): boolean => {
  try {
    new Intl.DateTimeFormat("en", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
};

const profileNameSchema = z
  .string()
  .trim()
  .min(1, "Enter a name")
  .max(100)
  .refine(withinUtf8Bytes(100), "Use no more than 100 bytes");

export const onboardingIntentCodeSchema = z.enum(["PROPERTY_OWNER", "CLEANER", "LAUNDRY_PARTNER"]);

export const customerSegmentCodeSchema = z.enum(["OWNER", "HOST"]);

const onboardingIntentListSchema = z
  .array(onboardingIntentCodeSchema)
  .min(1)
  .max(onboardingIntentCodeSchema.options.length)
  .superRefine((intents, context) => {
    if (new Set(intents).size !== intents.length) {
      context.addIssue({
        code: "custom",
        message: "Each onboarding intent can be selected only once",
      });
    }
  });

export const onboardingIntentStateSchema = z.strictObject({
  intents: onboardingIntentListSchema,
  customerSegment: customerSegmentCodeSchema.nullable(),
});

export const replaceOnboardingIntentsInputSchema = onboardingIntentStateSchema;

const cleanerOnboardingStateSchema = z.strictObject({
  databaseActive: z.literal(true),
  tokenActive: z.boolean(),
  tokenRefreshRequired: z.boolean(),
});

export const currentOnboardingIntentsResponseSchema = z.strictObject({
  status: z.literal("ok"),
  onboarding: onboardingIntentStateSchema,
});

export const replaceOnboardingIntentsResponseSchema = z.strictObject({
  status: z.enum(["updated", "unchanged"]),
  onboarding: onboardingIntentStateSchema,
  cleanerOnboarding: cleanerOnboardingStateSchema.nullable(),
});

const profileTimezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(withinUtf8Bytes(64), "Use no more than 64 bytes")
  .refine(validTimezone, "Enter a valid timezone");

export const updateCurrentUserPreferencesInputSchema = z.strictObject({
  localeCode: supportedProfileLocaleSchema,
  timezoneRegion: profileTimezoneSchema,
});

/**
 * Accepts a common US phone format and returns E.164 (`+1` + 10 digits), or
 * `null` when the input cannot be a US number. Separators and an optional `+1`
 * / leading `1` are tolerated; the stored value is always the canonical form.
 */
function normalizeUsPhoneE164(raw: string): string | null {
  const digits = raw.replace(/\D/gu, "");
  if (!/^1?\d{10}$/u.test(digits)) return null;
  return `+1${digits.length === 11 ? digits.slice(1) : digits}`;
}

export const provisionProfileSchema = z
  .strictObject({
    givenName: profileNameSchema,
    familyName: profileNameSchema,
    phoneE164: z
      .union([
        z
          .string()
          .trim()
          .transform(normalizeUsPhoneE164)
          .refine((value): value is string => value !== null, {
            message: "Enter a 10-digit US phone number.",
          }),
        z.null(),
      ])
      .optional()
      .transform((value) => value ?? null),
    localeCode: supportedProfileLocaleSchema,
    timezoneRegion: profileTimezoneSchema,
  })
  .refine(
    ({ givenName, familyName }) => utf8ByteLength(`${givenName} ${familyName}`) <= 200,
    "The combined name is too long",
  );

export const legalDocumentSchema = z
  .strictObject({
    id: idSchema,
    code: z.string().min(1),
    versionCode: z.string().min(1),
    documentType: z.enum(["TERMS", "PRIVACY"]),
    localeCode: supportedProfileLocaleSchema,
    contentUri: legalContentUrlSchema,
    contentSha256: sha256Schema,
    publishedAt: isoDateTimeSchema,
    required: z.literal(true),
    accepted: z.boolean(),
    acceptedAt: isoDateTimeSchema.nullable(),
  })
  .superRefine(({ code, documentType, accepted, acceptedAt }, context) => {
    const expectedCode = documentType === "TERMS" ? "CUSTOMER_TERMS" : "CUSTOMER_PRIVACY";
    if (code !== expectedCode) {
      context.addIssue({
        code: "custom",
        path: ["code"],
        message: `${documentType} must use the ${expectedCode} code`,
      });
    }
    if (accepted !== (acceptedAt !== null)) {
      context.addIssue({
        code: "custom",
        path: ["acceptedAt"],
        message: "Acceptance state and time must agree",
      });
    }
  });

export const legalAcceptanceInputSchema = z.strictObject({
  documentId: idSchema,
  versionCode: z.string().min(1),
  contentSha256: sha256Schema,
});

export const serviceCategoryListSchema = z.strictObject({
  data: z.array(serviceCategorySchema),
});
export const catalogServiceListSchema = z.strictObject({
  data: z.array(catalogServiceSchema),
});
export const linenItemTypeListSchema = z.strictObject({
  data: z.array(linenItemTypeSchema),
});
export const currentUserResponseSchema = z.strictObject({
  status: z.literal("ok"),
  user: currentUserSchema,
});
export const provisionCurrentUserResponseSchema = z.strictObject({
  status: z.enum(["created", "existing"]),
  user: currentUserSchema,
});
export const updateCurrentUserPreferencesResponseSchema = z.strictObject({
  status: z.literal("ok"),
  user: currentUserSchema,
});
const requiredLegalDocumentsSchema = z.array(legalDocumentSchema).length(2);

function validateRequiredLegalPublication(
  data: z.infer<typeof requiredLegalDocumentsSchema>,
  context: z.RefinementCtx,
): void {
  for (const requiredType of ["TERMS", "PRIVACY"] as const) {
    if (data.filter(({ documentType }) => documentType === requiredType).length !== 1) {
      context.addIssue({
        code: "custom",
        path: ["data"],
        message: `Exactly one ${requiredType} document is required`,
      });
    }
  }
  if (new Set(data.map(({ versionCode }) => versionCode)).size !== 1) {
    context.addIssue({
      code: "custom",
      path: ["data"],
      message: "The required documents must use one coordinated version",
    });
  }
  if (new Set(data.map(({ publishedAt }) => publishedAt)).size !== 1) {
    context.addIssue({
      code: "custom",
      path: ["data"],
      message: "The required documents must share one publication time",
    });
  }
}

export const currentLegalDocumentsResponseSchema = z
  .strictObject({
    data: requiredLegalDocumentsSchema,
    allAccepted: z.boolean(),
  })
  .superRefine(({ data, allAccepted }, context) => {
    validateRequiredLegalPublication(data, context);
    if (allAccepted !== data.every(({ accepted }) => accepted)) {
      context.addIssue({
        code: "custom",
        path: ["allAccepted"],
        message: "Overall acceptance must match the document acceptance states",
      });
    }
  });

export const publicCurrentLegalDocumentsResponseSchema = z
  .strictObject({ data: requiredLegalDocumentsSchema })
  .superRefine(({ data }, context) => {
    validateRequiredLegalPublication(data, context);
    for (const [index, document] of data.entries()) {
      if (document.accepted || document.acceptedAt !== null) {
        context.addIssue({
          code: "custom",
          path: ["data", index, "accepted"],
          message: "Public legal documents must be unaccepted",
        });
      }
    }
  });
export const legalAcceptanceResponseSchema = z.strictObject({
  status: z.enum(["accepted", "existing"]),
  acceptance: z.strictObject({
    documentId: idSchema,
    acceptedAt: isoDateTimeSchema,
  }),
});
const errorFieldSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^(?:[A-Za-z][A-Za-z0-9_-]*|[0-9]+)(?:\.(?:[A-Za-z][A-Za-z0-9_-]*|[0-9]+))*$/u)
  .refine(
    (field) =>
      field
        .split(".")
        .every((segment) => !["__proto__", "prototype", "constructor"].includes(segment)),
    "Unsafe error field",
  );
const validationConstraintsSchema = z.strictObject({
  minimum: z.number().finite().min(0).max(1_000_000_000_000).optional(),
  maximum: z.number().finite().min(0).max(1_000_000_000_000).optional(),
  multipleOf: z.number().finite().min(0).max(1_000_000_000_000).optional(),
  unit: z.enum(["value", "characters", "bytes", "items"]).optional(),
});
export const apiValidationIssueSchema = z.strictObject({
  location: z.enum(["body", "query", "path", "header"]),
  reason: z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/u),
  field: errorFieldSchema.optional(),
  constraints: validationConstraintsSchema.optional(),
});
export const errorResponseSchema = z.strictObject({
  error: z.strictObject({
    code: z.string().min(1),
    message: z.string().min(1),
    requestId: z.string(),
    field: errorFieldSchema.optional(),
    contractVersion: z.literal(2).optional(),
    issues: z.array(apiValidationIssueSchema).max(5).optional(),
  }),
});

export type ApiValidationIssue = z.infer<typeof apiValidationIssueSchema>;
export type ServiceCategory = z.infer<typeof serviceCategorySchema>;
export type CatalogServiceItem = z.infer<typeof catalogServiceSchema>;
export type LinenItemType = z.infer<typeof linenItemTypeSchema>;
export type CurrentUser = z.infer<typeof currentUserSchema>;
export type ClientRole = z.infer<typeof clientRoleSchema>;
export type ProvisionProfileInput = z.infer<typeof provisionProfileSchema>;
export type UpdateCurrentUserPreferencesInput = z.infer<
  typeof updateCurrentUserPreferencesInputSchema
>;
export type OnboardingIntentCode = z.infer<typeof onboardingIntentCodeSchema>;
export type CustomerSegmentCode = z.infer<typeof customerSegmentCodeSchema>;
export type OnboardingIntentState = z.infer<typeof onboardingIntentStateSchema>;
export type ReplaceOnboardingIntentsInput = z.infer<typeof replaceOnboardingIntentsInputSchema>;
export type ReplaceOnboardingIntentsResponse = z.infer<
  typeof replaceOnboardingIntentsResponseSchema
>;
export type LegalDocument = z.infer<typeof legalDocumentSchema>;
export type LegalAcceptanceInput = z.infer<typeof legalAcceptanceInputSchema>;
export type CurrentLegalDocumentsResponse = z.infer<typeof currentLegalDocumentsResponseSchema>;
export type PublicCurrentLegalDocumentsResponse = z.infer<
  typeof publicCurrentLegalDocumentsResponseSchema
>;
export type LegalAcceptanceResponse = z.infer<typeof legalAcceptanceResponseSchema>;
